import React from 'react';
import { ButtonGroup, Icon, IconButton, Menu } from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Badge from '@zg-rentals/multibrand-ui/lib/Badge';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import TransactionConstants from '@common/constants/TransactionConstants';
import ExpansionPanel from '@zg-rentals/multibrand-ui/lib/ExpansionPanel';
import MoveInCostsTypes from '@common/constants/MoveInCostsConstants';
import type { EditMoveInCostsInterface } from '@rental-manager/pages/Dashboard/types';
import type { IFeaturePermissionModel, IPropertyInfoModel, ITransactionModel } from '@rental-manager/models/Types';
import DepositDateTooltipComponent from '@rental-manager/components/StyledTooltip/DepositDateToolTip';
import MarkAsPaidAction from './MarkAsPaidAction';
import OfflineMarkNote from './OfflineMarkNote';
import UndoOfflineMarkAction from './UndoOfflineMarkAction';

type State = {
  expandedMoveInCostsSection: boolean;
};

type Props = {
  transaction: ITransactionModel;
  featurePermissions: IFeaturePermissionModel;
  goToCancelMoveInCostsModal: ({ term, rentTermsKey }: EditMoveInCostsInterface) => void;
  remindTenantToPayMIC: ({ tenantEmail }) => void;
  currentTerm: IPropertyInfoModel;
  nextTerm: IPropertyInfoModel;
};

class MoveInCostsRow extends React.Component<Props, State> {
  state = {
    expandedMoveInCostsSection: false,
  };

  goToCancelMoveInCostsModal = (): void => {
    const { transaction, nextTerm } = this.props;
    const { rentTermsKey } = transaction;
    const term = nextTerm?.rentTermsKey === rentTermsKey ? 'next' : 'current';
    this.props.goToCancelMoveInCostsModal({ term, rentTermsKey });
  };

  getPaymentSent = (status: string, dateCreated: Date | string): React.ReactNode => {
    if (status === TransactionConstants.STATUS.PENDING || status === TransactionConstants.STATUS.COMPLETE) {
      return <Text className="fs-exclude">Payment sent: {formatDate(dateCreated, 'M/d/yy')}</Text>;
    }
    return <></>;
  };

  getBankType = (category: string, accountType: string): string => {
    if (category === 'upcoming') return '';
    switch (accountType) {
      case 'credit':
        return 'Credit card';
      case 'debit':
        return 'Debit card';
      case 'bank_account':
        return 'ACH transfer';
      default:
        return '';
    }
  };

  toggleMoveInCosts = (): void => {
    this.setState({
      expandedMoveInCostsSection: !this.state.expandedMoveInCostsSection,
    });
  };

  remindTenantToPayMIC = (): void => {
    const tenantEmail = this.props.transaction.tenant.email;
    return this.props.remindTenantToPayMIC({ tenantEmail });
  };

  renderExpansionHeader = (): React.ReactNode => {
    const { landlordStatus, amount } = this.props.transaction;
    return (
      <Row className="pbn">
        <Col xs={8} sm={8} md={10} lg={10} xl={10} xxl={10} className="paymentDetailsRow-Mobile">
          <Row className="pbn">
            <Col>
              <Text className="fs-exclude" emphasize>
                Move-in costs
              </Text>
            </Col>
          </Row>
        </Col>
        <Col xs={4} sm={4} md={2} lg={2} xl={2} xxl={2} className="paymentDetailsRow-Mobile">
          <Row className="pbn">
            <Col className="Dashboard-Price-Badge-Padding txtR">
              <Text type="body" className="Dashboard-Price fs-exclude">
                {centsToDollars(amount)}
              </Text>
              {landlordStatus === TransactionConstants.STATUS.PENDING && (
                <Badge className="txtR fs-exclude" label="pending" type="info" />
              )}
              {landlordStatus === TransactionConstants.STATUS.PAST_DUE && (
                <Badge className="txtR fs-exclude" label="unpaid" type="fail" />
              )}
              {(landlordStatus === TransactionConstants.STATUS.COMPLETE ||
                landlordStatus === TransactionConstants.STATUS.MANUAL) && (
                <Badge className="txtR fs-exclude" label="paid" type="success" />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  renderNonExpandableMoveInCostsSection = (): React.ReactNode => {
    const {
      landlordStatus,
      tenant,
      paymentCategory,
      accountType,
      dueDate,
      dateRequested,
      dateCreated,
      dateReceived,
      showPaidOffline,
      groupKey,
      propertyId,
      amount,
      notes,
      manualPaymentNotes,
    } = this.props.transaction;
    const markAsPaidEnabled = getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false);
    return (
      <Row className="pbn">
        <Col xs={8} sm={8} md={10} lg={10} xl={10} xxl={10} className="paymentDetailsRow-Mobile">
          {landlordStatus === TransactionConstants.STATUS.PENDING && (
            <Text type="body" className="fs-exclude">
              Deposit date: {formatDate(dateReceived, 'M/d/yy')}
              <DepositDateTooltipComponent />
            </Text>
          )}
          {landlordStatus === TransactionConstants.STATUS.COMPLETE && (
            <Text className="fs-exclude">Deposited: {formatDate(dateReceived, 'M/d/yy')}</Text>
          )}
          <Text className="fs-exclude">{tenant.name}</Text>
          {paymentCategory === 'completed' && (
            <Text className="fs-exclude">{this.getBankType(paymentCategory, accountType)}</Text>
          )}
          {showPaidOffline && (
            <Text className="fs-exclude">{markAsPaidEnabled && dateReceived ? 'Marked as paid' : 'Paid offline'}</Text>
          )}
          {landlordStatus === TransactionConstants.STATUS.MANUAL && Boolean(dateReceived) && (
            <Text className="fs-exclude">
              Received: {formatDate(dateReceived, 'M/d/yy')} · Due date: {formatDate(dueDate, 'M/d/yy')}
            </Text>
          )}
          {showPaidOffline && markAsPaidEnabled && Boolean(dateReceived) && manualPaymentNotes && (
            <OfflineMarkNote note={manualPaymentNotes} />
          )}
          {showPaidOffline && markAsPaidEnabled && Boolean(dateReceived) && (
            <UndoOfflineMarkAction
              propertyId={propertyId}
              notes={notes}
              target={{ kind: 'oneTimePayment', groupKey }}
            />
          )}
          {!(landlordStatus === TransactionConstants.STATUS.MANUAL && Boolean(dateReceived)) && (
            <Text className="fs-exclude">Due date: {formatDate(dueDate, 'M/d/yy')}</Text>
          )}
          {(paymentCategory === 'upcoming' || paymentCategory === 'pastDue') && (
            <Text className="fs-exclude">Requested {formatDate(dateRequested, 'M/d/yy')}</Text>
          )}
          {this.getPaymentSent(landlordStatus, dateCreated)}
          {(landlordStatus === TransactionConstants.STATUS.UNPAID ||
            landlordStatus === TransactionConstants.STATUS.PAST_DUE) &&
            (markAsPaidEnabled ? (
              <ButtonGroup aria-label="Payment actions">
                <MarkAsPaidAction
                  heading="Move-in costs"
                  subheading={`Due ${formatDate(dueDate, 'MMM d, yyyy')} · ${tenant.name}`}
                  amount={centsToDollars(amount)}
                  propertyId={propertyId}
                  target={{ kind: 'oneTimePayment', groupKey }}
                />
                <Menu
                  trigger={
                    <IconButton size="sm" tone="brand" emphasis="outlined" title="More payment actions">
                      <Icon>
                        <IconMoreFilled />
                      </Icon>
                    </IconButton>
                  }
                >
                  <Menu.Item onClick={this.goToCancelMoveInCostsModal}>
                    <Menu.ItemLabel>Cancel</Menu.ItemLabel>
                  </Menu.Item>
                  <Menu.Item onClick={this.remindTenantToPayMIC}>
                    <Menu.ItemLabel>Remind the tenant</Menu.ItemLabel>
                  </Menu.Item>
                </Menu>
              </ButtonGroup>
            ) : (
              <Text>
                <Link onClick={this.goToCancelMoveInCostsModal}>Cancel</Link>
                <span className="inset-xs">|</span>
                <Link onClick={this.remindTenantToPayMIC}>Remind the tenant</Link>
              </Text>
            ))}
        </Col>
      </Row>
    );
  };

  renderExpandedMoveInCostsSection = (): React.ReactNode => {
    const { moveInCostsItems } = this.props.transaction;
    return (
      <>
        {moveInCostsItems!.map((moveInCostItem, i) => (
          <Row className="pbn" key={i.toString()}>
            <Col xs={8} sm={8} md={10} lg={10} xl={10} xxl={10} className="paymentDetailsRow-Mobile">
              <Row className="pbn">
                <Col>
                  {!moveInCostItem.subType && (
                    <Text className="fs-exclude" emphasize>
                      {MoveInCostsTypes[moveInCostItem.type]}
                    </Text>
                  )}
                  {moveInCostItem.subType && (
                    <Text className="fs-exclude" emphasize>
                      {moveInCostItem.subType}
                    </Text>
                  )}
                  <Text type="body-small" className="fs-exclude">
                    {moveInCostItem.notes}
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col xs={4} sm={4} md={2} lg={2} xl={2} xxl={2} className="paymentDetailsRow-Mobile">
              <Row className="pbn">
                <Col className="Transaction-expansion-price-padding txtR">
                  <Text type="body" className="Dashboard-Price fs-exclude">
                    {centsToDollars(moveInCostItem.amount)}
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>
        ))}
      </>
    );
  };

  render(): React.ReactNode {
    return (
      <>
        <ExpansionPanel
          isExpanded={this.state.expandedMoveInCostsSection}
          onHeaderClick={this.toggleMoveInCosts}
          header={this.renderExpansionHeader()}
          expandedContent={this.renderExpandedMoveInCostsSection()}
          className="Transaction-expansion-row"
        />
        {this.renderNonExpandableMoveInCostsSection()}
      </>
    );
  }
}

export default MoveInCostsRow;
